import { compact, uniq, uniqBy } from 'lodash';

import { DocumentSearchResult } from '@/modules/pdf/DocumentSearch/DocumentSearch.types';
import { AugmentedCreatePresentationItemCitationInput } from '@/types/schema';

import { ClientPresentationV2ItemCitation } from '../types/ClientPresentation.PresentationBundleType';

/** An id the citation drawer can be opened for, paired with the citations it
 *  should show. Cards and rows both resolve through one lookup, since ids are
 *  unique across a bundle. */
export interface CitedItem {
  id: string;
  citations: ClientPresentationV2ItemCitation[];
}

/** Identifies the span a citation points at, independent of its row. Two rows
 *  grounded in the same paragraphs of the same document produce separate
 *  citation rows with distinct ids, so the id can't be the dedupe key — the
 *  (document, paragraph set) pair is what actually distinguishes a source.
 *  Paragraph ids are sorted so ordering differences don't read as distinct. */
function citationSpanKey(citation: ClientPresentationV2ItemCitation): string {
  const paragraphIds = (citation.fileParagraphs ?? [])
    .map((paragraph) => paragraph.id)
    .sort();
  return `${citation.document.id}|${paragraphIds.join(',')}`;
}

/**
 * Builds the citation lookup for a slide whose cards hold rows: each row keyed
 * on its own citations, plus each card keyed on the union of its rows' — so the
 * card's toolbar opens the drawer on everything cited inside it.
 *
 * Card citations are concatenated in row order, so the drawer walks the sources
 * top-to-bottom the way the card reads, then deduped by span: the card-level
 * drawer doesn't attribute a source back to a row, so the same span surfacing
 * once per citing row would just be repeated highlights with nothing to
 * distinguish them. The first citing row's copy wins, keeping row order intact.
 * Rows keep their own citations verbatim.
 *
 * Cards whose rows are all uncited aggregate to an empty list, which
 * `useResolveViewCitations` turns into "no button".
 */
export function getCardAndRowCitations<TCard extends { id: string }>(
  cards: TCard[],
  getRows: (card: TCard) => CitedItem[]
): CitedItem[] {
  return cards.flatMap((card) => {
    const rows = getRows(card);
    return [
      {
        id: card.id,
        citations: uniqBy(
          rows.flatMap((row) => row.citations),
          citationSpanKey
        ),
      },
      ...rows,
    ];
  });
}

/** Everything the citation drawer needs to open on the right document with
 *  the cited spans highlighted. */
export interface PresentationCitationTarget {
  /** Documents to offer in the drawer's document selector, ordered so the
   *  document holding the first annotation comes first. */
  documentIds: string[];
  /** Highlight regions across all cited documents, in citation order. */
  annotations: DocumentSearchResult[];
}

/**
 * Flattens an item's citations into drawer inputs.
 *
 * Each citation names a source document plus the file paragraphs whose text
 * grounds the item, and each paragraph carries its own bounding regions — so
 * one citation can produce many annotations. Annotations key off the
 * paragraph's own `file.pdfDocument`, which is what the PDF viewer renders;
 * this can differ from the citation's `document` when the span was extracted
 * from a derived file. Mirrors `suggestionEvidenceAdapter` for AI-suggestion
 * evidence.
 *
 * Citations without a resolvable paragraph document still contribute their
 * `document` to `documentIds` so the drawer can at least open the source
 * document, just without highlights.
 */
export function getPresentationCitationTarget(
  citations: ClientPresentationV2ItemCitation[] | null | undefined
): PresentationCitationTarget {
  const annotations = (citations ?? []).flatMap((citation) =>
    (citation.fileParagraphs ?? []).flatMap((paragraph) => {
      const paragraphDocumentId = paragraph.file?.pdfDocument?.id;
      if (!paragraphDocumentId) return [];

      return paragraph.boundingRegions.map((region) => ({
        documentID: paragraphDocumentId,
        PageNumber: region.PageNumber,
        Polygon: region.Polygon,
      }));
    })
  );

  // Annotation documents lead so the drawer opens on a document that actually
  // has highlights; the citations' own documents fill in the rest.
  const documentIds = uniq(
    compact([
      ...annotations.map((annotation) => annotation.documentID),
      ...(citations ?? []).map((citation) => citation.document.id),
    ])
  );

  return { documentIds, annotations };
}

/**
 * Serializes an item's citations for the nested `withCitations` create on that
 * item's augmented input.
 *
 * Every save recreates the whole bundle (`clearBundles: true`), which
 * cascade-deletes the old citation rows along with their items — so the client
 * has to re-send the (document, span) pairs it read, or the grounding the
 * backend generated is lost on the first edit. The rows carry no scalar data of
 * their own, so re-creating them from `documentID` + `fileParagraphIDs` is
 * lossless; the ids are freshly minted server-side each time.
 *
 * Citations whose paragraph list came back empty are still worth re-sending —
 * they preserve the document-level link even without a span to highlight.
 *
 * TODO(T1-6071): once saves stop recreating the whole bundle, the citation rows
 * survive on their own and this round-trip can go away entirely.
 */
export function mapCitationsToCreateInput(
  citations: ClientPresentationV2ItemCitation[] | null | undefined,
  householdID: string
): AugmentedCreatePresentationItemCitationInput[] {
  return (citations ?? []).map((citation) => ({
    create: {
      householdID,
      documentID: citation.document.id,
      fileParagraphIDs: (citation.fileParagraphs ?? []).map(
        (paragraph) => paragraph.id
      ),
    },
  }));
}
